import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "generative-ai",
  name: "生成AI",
  nameEn: "Generative AI",
  seo: {
    title: "生成AIを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "ChatGPT・Claude・Geminiなどの生成AIを活用するスキルを習得。ゆめさくエンジニアならプロンプト設計からAIツール連携まで実践的に学べます。",
    url: "/columns/generative-ai-programming-school",
  },
  hero: {
    title: "生成AIを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Generative AI",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "生成AI" },
  ],
  overview: {
    heading: "生成AIとは？",
    paragraphs: [
      "生成AI（Generative AI）とは、テキスト・画像・音声・コードなどを自動で生成できる人工知能の総称です。2022年末のChatGPTの登場以降、ビジネスから日常生活まであらゆる場面で活用が広がっています。",
      "ChatGPT（OpenAI）、Claude（Anthropic）、Gemini（Google）などの大規模言語モデル（LLM）を使いこなすスキルは、エンジニアだけでなくすべてのビジネスパーソンにとって重要な時代になっています。",
      "プロンプトの書き方一つで出力の品質が大きく変わるため、効果的なプロンプト設計スキルの価値が急速に高まっています。また、AIをアプリケーションに組み込む技術の需要も爆発的に増加しています。",
    ],
    useCases: [
      {
        label: "コーディングアシスタント",
        description:
          "GitHub Copilot、Claude Code、Cursor AIなどを活用し、コードの自動生成・バグの発見・リファクタリング提案で開発効率を飛躍的に向上させます。",
      },
      {
        label: "コンテンツ作成の効率化",
        description:
          "ブログ記事・メールの下書き・企画書の作成など、テキストコンテンツの初稿を生成AIに任せることで、作業時間を大幅に短縮できます。",
      },
      {
        label: "データ分析・レポート作成",
        description:
          "CSVデータの分析、グラフ作成、レポートの要約など、生成AIを使って高品質な分析レポートを短時間で作成できます。",
      },
      {
        label: "AIチャットボットの構築",
        description:
          "自社のナレッジベースやFAQを基にしたカスタムAIチャットボットを構築し、カスタマーサポートの自動化や社内問い合わせ対応を効率化します。",
      },
    ],
  },
  whyLearn: {
    heading: "生成AIを学ぶべき3つの理由",
    items: [
      {
        title: "あらゆる職種の生産性を劇的に向上",
        description:
          "生成AIを活用できるかどうかで仕事の生産性に数倍の差がつく時代になっています。プログラミングに限らず、文書作成・データ分析・デザインなど、あらゆる業務でAI活用スキルは大きな武器になります。",
      },
      {
        title: "AI活用人材の需要が爆発的に増加",
        description:
          "企業のAI導入が加速する中、AIを使いこなせる人材は圧倒的に不足しています。プロンプトエンジニアリングやAIツール連携のスキルは、今後数年間で最も需要が高まるスキルの一つです。",
      },
      {
        title: "プログラミング学習そのものが加速する",
        description:
          "生成AIは最高のプログラミング学習パートナーでもあります。わからないコードの解説、エラーの原因特定、ベストプラクティスの提案など、AIを活用することで学習効率が格段に向上します。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアで生成AIを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではPhase 4の基礎として生成AIの活用スキルを学びます。単にAIに質問するだけでなく、効果的なプロンプト設計、出力の評価・改善、アプリケーションへのAI統合まで体系的に習得します。",
      "ChatGPT・Claude・Geminiなど複数のAIモデルの特徴を理解し、目的に応じて使い分けるスキルを養います。また、AIの限界や倫理的な課題についても学び、責任あるAI活用ができるエンジニアを目指します。",
    ],
    topics: [
      "生成AIの仕組み（LLMの基本原理）",
      "効果的なプロンプト設計（Zero-shot, Few-shot, CoT）",
      "ChatGPT・Claude・Geminiの比較と使い分け",
      "GitHub Copilotを使った開発効率化",
      "AIアプリケーション開発（API連携）",
      "AIの限界・ハルシネーション対策・倫理",
    ],
  },
  careerPaths: {
    heading: "生成AIを活かせるキャリア",
    items: [
      {
        title: "AIエンジニア",
        description:
          "生成AIをアプリケーションに組み込む開発を担当します。RAG（検索拡張生成）システムの構築、LLMのファインチューニング、AIエージェントの開発など、最先端の技術に携わります。",
        salary: "600〜1,200万円",
      },
      {
        title: "プロンプトエンジニア",
        description:
          "ビジネス課題に対して最適なプロンプトを設計し、AI出力の品質を最大化するスペシャリストです。非エンジニア部門へのAI導入支援や、社内のAI活用ガイドライン策定なども担います。",
        salary: "500〜900万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "情報が散乱して体系的な学習が困難",
        luagate: "2〜3ヶ月（基礎から応用まで段階的に）",
      },
      {
        label: "挫折率",
        selfStudy: "表面的な使い方で終わり応用力がつかない",
        luagate: "プロンプト設計の原則から論理的に学習",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "ChatGPTに質問するだけで開発に活かせない",
        luagate: "AIを組み込んだアプリケーション開発を実践",
      },
      {
        label: "質問対応",
        selfStudy: "AI活用のベストプラクティスがわからない",
        luagate: "AI活用経験豊富なメンターが指導",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "生成AIを学ぶのにプログラミングの知識は必要ですか？",
      answer:
        "プロンプト設計やAIツールの活用はプログラミング未経験でも学べますが、AIをアプリケーションに組み込むにはプログラミングスキルが必要です。ゆめさくエンジニアではPhase 1〜3でプログラミングの基礎を学んだ後にAIフェーズに入るため、段階的にスキルを積み上げられます。",
    },
    {
      question: "ChatGPTとClaudeはどちらがおすすめですか？",
      answer:
        "用途によって強みが異なります。ChatGPTは汎用性が高くプラグインが充実、Claudeは長文理解とコーディングに強い特徴があります。ゆめさくエンジニアでは複数モデルの特性を比較しながら、状況に応じた使い分けを学びます。",
    },
    {
      question: "生成AIの分野は変化が速すぎて学んでもすぐ陳腐化しませんか？",
      answer:
        "ツールは変わっても、プロンプト設計の原則やAI活用の考え方は普遍的です。ゆめさくエンジニアでは特定のツールの使い方ではなく、原理原則と応用力を重視しているため、新しいモデルやツールが登場しても柔軟に対応できます。",
    },
    {
      question: "AIに仕事を奪われるのではなく、AIを使う側になれますか？",
      answer:
        "まさにそれがゆめさくエンジニアで生成AIを学ぶ目的です。AIは人間の仕事を奪うのではなく、AI活用スキルを持つ人と持たない人の間で大きな生産性の差が生まれています。AIを使いこなせるエンジニアは、今後ますます市場価値が高まります。",
    },
    {
      question: "AI関連の仕事に就くために資格は必要ですか？",
      answer:
        "AI関連に特化した必須資格はありませんが、AIを活用したプロダクトやプロジェクトの実績がポートフォリオとして評価されます。ゆめさくエンジニアでは生成AIを組み込んだアプリケーション開発の実績を作れるため、転職活動で具体的な成果をアピールできます。",
    },
  ],
  relatedLanguages: [
    { slug: "python", name: "Python" },
    { slug: "rag-llm", name: "RAG / LLM" },
    { slug: "prompt-engineering", name: "プロンプトエンジニアリング" },
    { slug: "nocode-ai", name: "ノーコードAI" },
  ],
};
